import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME, ProspectState } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { EligibilityManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/eligibility-manager.service';
import { EnrollManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/enroll-manager.service';
import { PaymentManagerService, ErroredStatus } from '../../services/payment-manager.service';
import { PaymentPresenterService } from '../../services/payment-presenter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankFormControlsAsJson, CreditCardFormControlsAsJson, ON_EXCHANGE_PAYMENT_METHODS } from 'src/app/angular-elements/shared/interfaces/on-exchange-enrollment-payment-form.interface';

/** Abstract Controls */
interface BillingAddressFormControls {
  addressLine1: AbstractControl;
  addressLine2: AbstractControl;
  city: AbstractControl;
  state: AbstractControl;
  zipcode: AbstractControl;
}
interface CreditCardFormControls {
  ccType: AbstractControl;
  ccNumEnc: AbstractControl;
  ccExpDtMonth: AbstractControl;
  ccExpDtYear: AbstractControl;
  name: AbstractControl;
  billingAddress: UntypedFormGroup;
}
interface BankFormControls {
  routingNumber: AbstractControl;
  accountNumber: AbstractControl;
  primaryNameOnAccount: AbstractControl;
  billingAddress: UntypedFormGroup;
}

@FormComponentLookup('marketplace-payment-info')
@Component({
    selector: 'app-marketplace-payment-info',
    templateUrl: './marketplace-payment-info.component.html',
    styleUrls: ['./marketplace-payment-info.component.scss'],
    standalone: false
})
export class MarketplacePaymentInfoComponent extends FormEntryComponentClass implements OnInit, OnDestroy {

  showBillMeLaterForm: boolean;

  showBankForm: boolean;
  bankForm: UntypedFormGroup;
  isAccountNumberVisible: boolean;
  bankFormChangesSubscription: Subscription;

  showCreditCardForm: boolean;
  creditCardForm: UntypedFormGroup;
  isCreditCardNumberVisible: boolean;
  creditCardFormChangesSubscription: Subscription;
  cardTypeOptions = [
    { label: 'Visa', value: 'VI' },
    { label: 'Master Card', value: 'MC' },
    { label: 'Discover', value: 'DI' },
    { label: 'American Express', value: 'AX' }
  ];

  billMeLaterInfo: { label: string, value: any }[];

  prospect: ProspectState;
  prospectId: string;

  constructor(
    private paymentManagerService: PaymentManagerService,
    private eligibilityManagerService: EligibilityManagerService,
    private enrollManagerService: EnrollManagerService,
    private fb: UntypedFormBuilder,
    private paymentPresenterService: PaymentPresenterService,
    private snackBar: MatSnackBar,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId);

    if (matchedProspects.length > 0) {
      const prospect = matchedProspects[0];
      this.prospect = prospect;
      await this.eligibilityManagerService.get(prospect.prospectId);
      this.prospectId = prospect.prospectId;
      this.setupForms();


      /** Hide form-driven next button, we'll make our own */
      const hideNextButtonEvent = {
        formControlName: null,
        type: 'hideNextButton',
        value: null
      };
      this.formStreamTrigger.triggerFormStream(hideNextButtonEvent);

      /** Hide form-driven back button, we'll make our own */
      const hideBackButtonEvent = {
        formControlName: null,
        type: 'hideBackButton',
        value: null
      };
      this.formStreamTrigger.triggerFormStream(hideBackButtonEvent);


      this.setupSubscription();
    }
  }

  ngOnDestroy(): void {
    if (this.bankFormChangesSubscription) {
      this.bankFormChangesSubscription.unsubscribe();
    }
    if (this.creditCardFormChangesSubscription) {
      this.creditCardFormChangesSubscription.unsubscribe();
    }
  }

  trackByIndex(index: number, obj: { label: string; value: any; } ): number {
    return index;
  }

  get bankFormControls(): BankFormControls {
    return this.bankForm.controls as unknown as BankFormControls;
  }
  get bankFormBillingAddressControls(): BillingAddressFormControls {
    return this.bankFormControls.billingAddress.controls as unknown as BillingAddressFormControls;
  }
  get creditCardFormControls(): CreditCardFormControls {
    return this.creditCardForm.controls as unknown as CreditCardFormControls;
  }
  get creditCardFormBillingAddressControls(): BillingAddressFormControls {
    return this.creditCardFormControls.billingAddress.controls as unknown as BillingAddressFormControls;
  }
  get getCreditCardValuesAsJson(): CreditCardFormControlsAsJson {
    return this.creditCardForm.value as unknown as CreditCardFormControlsAsJson;
  }
  get getBankFormValuesAsJson(): BankFormControlsAsJson {
    return this.bankForm.value as unknown as BankFormControlsAsJson;
  }

  setupForms() {

    this.bankForm = this.fb.group({
      routingNumber: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('^[0-9]{9}$'), Validators.maxLength(9)]),
      accountNumber: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('^[0-9]{4,17}$'), Validators.maxLength(17)]), // mask
      primaryNameOnAccount: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT)]),
      billingAddress: this.fb.group({
        addressLine1: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(55)]),
        addressLine2: this.fb.control('', [ /** this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT) */, Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(55)]),
        city: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(30)]),
        state: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('[A-Z]{2}'),]),
        zipcode: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT), Validators.pattern('^[0-9]{5}$'), Validators.maxLength(5)])
      })
    });

    this.creditCardForm = this.fb.group({
      ccType: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD)]),
      ccNumEnc: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.maxLength(16)]),
      ccExpDtMonth: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD)]),
      ccExpDtYear: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.maxLength(4), Validators.minLength(4)]),
      name: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.maxLength(90)]),
      billingAddress: this.fb.group({
        addressLine1: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(55)]),
        addressLine2: this.fb.control('', [ /** this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD) */, Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(55)]),
        city: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.pattern('^(?!\\s{1,}).*?$'), Validators.maxLength(30)]),
        state: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.pattern('[A-Z]{2}'),]),
        zipcode: this.fb.control('', [this.getRequiredValidator(ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD), Validators.pattern('^[0-9]{5}$'), Validators.maxLength(5)])
      })
    });

    this.billMeLaterInfo = [
      { label: 'Payment ID #', value: this.enrollManagerService.getPaymentTransactionID() },
      { label: 'Marketplace Policy Number', value: this.enrollManagerService.getFFMPolicyNumber() },
      { label: 'Monthly Premium', value: '$' + (Number(this.prospect.individualResponsibilityAmount).toFixed(2)) },
      { label: 'Payment Due Date', value: this.paymentPresenterService.dueDate(this.prospect.data.effectiveDate) },
      { label: 'Effective Date', value: this.prospect.data.effectiveDate }
    ];

  }

  setupSubscription() {

    this.bankFormChangesSubscription = this.bankForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((v: BankFormControlsAsJson) => {

        const dynamicObject = {};
        if (!dynamicObject[this.entry.formControlAttributes.formControlName]) {
          dynamicObject[this.entry.formControlAttributes.formControlName] = {};
        }
        dynamicObject[this.entry.formControlAttributes.formControlName][ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT] = v;
        dynamicObject[this.entry.formControlAttributes.formControlName][ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD] = null;

        /** Update global form (trigger save to state) */
        this.form.patchValue(dynamicObject);
      });

    this.creditCardFormChangesSubscription = this.creditCardForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((v: CreditCardFormControlsAsJson) => {

        const dynamicObject = {};
        if (!dynamicObject[this.entry.formControlAttributes.formControlName]) {
          dynamicObject[this.entry.formControlAttributes.formControlName] = {};
        }
        dynamicObject[this.entry.formControlAttributes.formControlName][ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT] = null;
        dynamicObject[this.entry.formControlAttributes.formControlName][ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD] = v;

        /** Update global form (trigger save to state) */
        this.form.patchValue(dynamicObject);
      });
  }

  getRequiredValidator(controlPaymentType: ON_EXCHANGE_PAYMENT_METHODS) {
    if (
      controlPaymentType === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT &&
      this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT
    ) {
      this.showBankForm = true;
      this.showCreditCardForm = false;
      this.showBillMeLaterForm = false;

      return Validators.required;
    } else if (
      controlPaymentType === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT &&
      this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD
    ) {
      this.showBankForm = false;
      this.showCreditCardForm = true;
      this.showBillMeLaterForm = false;

      return Validators.required;
    } else if (
      this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.BILL_ME_LATER
    ) {
      this.showBankForm = false;
      this.showCreditCardForm = false;
      this.showBillMeLaterForm = true;
    }

    return Validators.nullValidator;
  }

  forceStateToUpperCase(form: UntypedFormGroup) {
    (form.controls['billingAddress'] as UntypedFormGroup).controls['state'].setValue((form.controls['billingAddress'] as UntypedFormGroup).controls['state'].value.toUpperCase());
    form.updateValueAndValidity();
  }

  psuedoSubmit() {

    this.paymentManagerService.setPaymentData(
      this.getCreditCardValuesAsJson,
      this.getBankFormValuesAsJson
    );
  }

  goBack() {
    const event = {
      formControlName: null,
      type: 'triggerBackPage',
      value: null
    };
    this.formStreamTrigger.triggerFormStream(event);
  }


  async submit() {
    this.bankForm.updateValueAndValidity();
    this.creditCardForm.updateValueAndValidity();

    if (
      (this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT && this.bankForm.valid) ||
      (this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD && this.creditCardForm.valid) ||
      (this.paymentManagerService.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.BILL_ME_LATER)
    ) {
      this.paymentManagerService.setPaymentData(
        this.getCreditCardValuesAsJson,
        this.getBankFormValuesAsJson
      );
      const paymentResponse = await this.paymentManagerService.submitApplicationOnX(this.prospectId);
      if (paymentResponse.code === 200) {
        const event = {
          formControlName: null,
          type: 'onExchangePaymentProcessedGoToConfirmationPage',
          value: null
        };
        this.formStreamTrigger.triggerFormStream(event);
      } else {
        const alertDuration = 5000; // ms
        this.snackBar.open(
          (paymentResponse as ErroredStatus).error, 'OK', {
          duration: alertDuration,
          panelClass: 'positive-panel-class'
        });
      }
    }
  }

}
